// @ts-check

/**
 * The protocol between `<sp-finder-filter-bar>` and the `<sp-finder-filter>` elements slotted into
 * it: the filter announces itself and reports its state, and the bar acts on a filter only through
 * the functions the filter handed over. Both events are dispatched on the filter's host through
 * `useHostEmit().emitBubbling` — `bubbles: true`, `composed: false`, the payload at `detail[0]`.
 *
 * A filter with no `sp-finder-filter-bar` ancestor dispatches neither event.
 */

/** The bar's tag, the ancestor a filter resolves with `closest()`. */
export const FINDER_FILTER_BAR_TAG = "sp-finder-filter-bar";

/**
 * The ancestors that give a filter its chip chrome, as a `closest()` selector. Beside the bar's own
 * tag it names the two legacy containers a filter still sits in — the events page's `sp-filter-bar`
 * and the finder header's `doctor-finder-header-filters` — which leave with
 * https://github.com/combinaut/sparkle/issues/30609 and
 * https://github.com/combinaut/sparkle/issues/30611.
 */
export const FILTER_CHROME_ANCESTORS = `${FINDER_FILTER_BAR_TAG}, sp-filter-bar, doctor-finder-header-filters`;

/** The attribute a filter carries while it wears the chip chrome, and the leaf filters style from. */
export const FILTER_CHROME_ATTRIBUTE = "chrome";

/** The {@link FILTER_CHROME_ATTRIBUTE} value for the chip chrome. */
export const BAR_CHROME = "bar";

/**
 * Dispatched by a filter when its bar is mount-ready: at the filter's own mount when the bar
 * already carries `MOUNT_READY_MARKER`, and on every `MOUNT_READY_EVENT` the bar dispatches
 * afterwards (`#ce/framework/mount-ready`).
 * `detail[0]` is a {@link FinderFilterConnectPayload}.
 */
export const FINDER_FILTER_CONNECT_EVENT = "sp:finder-filter-connect";

/**
 * Dispatched by a filter after any field of its state changes, once the change has flushed.
 * `detail[0]` is a full {@link FinderFilterState}; the state at mount rides in the connect payload.
 */
export const FINDER_FILTER_STATE_EVENT = "sp:finder-filter-state";

/**
 * @typedef {object} FinderFilterState
 * @property {boolean} active - a value is applied.
 * @property {boolean} open - the filter's surface is showing.
 * @property {boolean} withheld - the filter depends on something not in place, and holds no value
 *   of its own, so it hides itself. A bar leaves a withheld filter out.
 * @property {boolean} clearable - the filter permits clearing. A capability, independent of
 *   `active`: an inactive clearable filter reports `true` with nothing to clear.
 */

/**
 * Closures over the filter's own model. Each returns nothing, is safe to call at any time, and is
 * a no-op when the filter is already in the requested state.
 *
 * @typedef {object} FinderFilterActions
 * @property {() => void} open - opens the filter's surface. A withheld filter opens but stays
 *   hidden.
 * @property {() => void} close - closes the filter's surface.
 * @property {() => void} clear - clears the applied value; a no-op when nothing is applied or the
 *   filter is not clearable.
 */

/**
 * @typedef {object} FinderFilterConnectPayload
 * @property {string} name - the `name` attribute; several filters in one bar may share it.
 * @property {string} label - the display label.
 * @property {string | undefined} icon - the `sp-icon` name.
 * @property {FinderFilterState} state - the state at announcement.
 * @property {FinderFilterActions} actions
 */
